/**
 * Turns a confirmed chat / watch proposal into the existing write APIs.
 */

import {
  createBudgetItem,
  createSortingRule,
  restoreTransaction,
  setAllocations,
  trashTransaction,
  updateBudgetItem,
  type ChatProposal,
} from '../api/client';
import type { Budget } from '../budget/useBudget';

export function proposalConfirmLabel(proposal: ChatProposal): string {
  if (proposal.kind === 'categorize' && typeof proposal.payload.budgetItemName === 'string') {
    return `File under ${proposal.payload.budgetItemName}`;
  }
  if (proposal.kind === 'sorting_rule') return 'Make rule';
  if (proposal.kind === 'trash') return 'Move to trash';
  if (proposal.kind === 'create_envelope') return 'Create envelope';
  if (proposal.kind === 'create_bill') return 'Create bill';
  if (proposal.kind === 'update_envelope' || proposal.kind === 'update_bill') return 'Update';
  return 'Confirm';
}

export async function applyProposal(proposal: ChatProposal, budget: Budget): Promise<void> {
  const payload = proposal.payload;
  switch (proposal.kind) {
    case 'categorize': {
      const transactionId = String(payload.transactionId || '');
      const budgetItemId = String(payload.budgetItemId || '');
      const amount = Number(payload.amount);
      const tx = budget.transactions.find((row) => row.id === transactionId);
      await setAllocations(
        transactionId,
        [{ budgetItemId, amount: Number.isFinite(amount) ? amount : Math.abs(tx?.amount ?? 0) }],
        tx?.version
      );
      return;
    }
    case 'split': {
      const transactionId = String(payload.transactionId || '');
      const allocations = Array.isArray(payload.allocations)
        ? (payload.allocations as Array<{ budgetItemId: string; amount: number; notes?: string }>)
        : [];
      const tx = budget.transactions.find((row) => row.id === transactionId);
      await setAllocations(
        transactionId,
        allocations.map((row) => ({
          budgetItemId: row.budgetItemId,
          amount: row.amount,
          notes: row.notes ?? null,
        })),
        tx?.version
      );
      return;
    }
    case 'create_envelope':
    case 'create_bill': {
      await createBudgetItem({
        kind: proposal.kind === 'create_bill' ? 'bill' : 'envelope',
        name: String(payload.name || 'Untitled'),
        icon: String(payload.icon || (proposal.kind === 'create_bill' ? 'calendar' : 'envelope.fill')),
        color: String(payload.color || 'kai'),
        category: String(payload.category || 'General'),
        perPaycheckAmount: Number(payload.perPaycheckAmount) || 0,
        dueDay: typeof payload.dueDay === 'number' ? payload.dueDay : null,
        notes: typeof payload.notes === 'string' ? payload.notes : null,
      });
      return;
    }
    case 'update_envelope':
    case 'update_bill': {
      const id = String(payload.id || '');
      const item = budget.itemsById.get(id);
      if (!item) throw new Error('Envelope not found.');
      await updateBudgetItem(id, {
        expectedVersion: item.version,
        name: typeof payload.name === 'string' ? payload.name : item.name,
        perPaycheckAmount:
          typeof payload.perPaycheckAmount === 'number'
            ? payload.perPaycheckAmount
            : item.perPaycheckAmount,
        notes: typeof payload.notes === 'string' ? payload.notes : item.notes,
      });
      return;
    }
    case 'sorting_rule': {
      await createSortingRule({
        keyword: String(payload.keyword || ''),
        budgetItemId: String(payload.budgetItemId || ''),
        createdFromDescription: typeof payload.createdFromDescription === 'string'
          ? payload.createdFromDescription
          : null,
      });
      return;
    }
    case 'trash': {
      const transactionId = String(payload.transactionId || '');
      const tx = budget.transactions.find((row) => row.id === transactionId);
      if (!tx) throw new Error('Charge not found.');
      await trashTransaction(transactionId, tx.version, typeof payload.reason === 'string' ? payload.reason : undefined);
      return;
    }
    case 'restore': {
      const transactionId = String(payload.transactionId || '');
      const tx = budget.transactions.find((row) => row.id === transactionId);
      if (!tx) throw new Error('Charge not found.');
      await restoreTransaction(transactionId, tx.version);
      return;
    }
    default:
      throw new Error(`Unsupported change: ${proposal.kind}`);
  }
}
